import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react'
import { ThemeProvider } from '@mui/material'
import type { UiPalette } from '@theme/uiPalette.types'
import { syncPaletteToCssVars } from '@theme/cssVars'
import { syncPaletteToLegacyCssVars } from '@theme/legacyCssVars'
import { createAppTheme } from '@theme/createAppTheme'
import {
  persistThemePresetId,
  presetPaletteFromId,
  readStoredThemePresetId,
  type ThemePresetId,
} from '@theme/themePresets'

type ThemePreferenceValue = {
  presetId: ThemePresetId
  setPresetId: (id: ThemePresetId) => void
  palette: UiPalette
}

const ThemePreferenceContext = createContext<ThemePreferenceValue | null>(null)

export const ThemePreferenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [presetId, setStoredPresetId] = useState<ThemePresetId>(() => readStoredThemePresetId())

  const palette = useMemo(() => presetPaletteFromId(presetId), [presetId])

  useLayoutEffect(() => {
    syncPaletteToCssVars(palette as unknown as Record<string, unknown>)
    syncPaletteToLegacyCssVars(palette)
  }, [palette])

  const theme = useMemo(() => createAppTheme(palette), [palette])

  const setPresetId = useCallback((id: ThemePresetId) => {
    persistThemePresetId(id)
    setStoredPresetId(id)
  }, [])

  const value = useMemo(
    (): ThemePreferenceValue => ({ presetId, setPresetId, palette }),
    [palette, presetId, setPresetId],
  )

  return (
    <ThemePreferenceContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemePreferenceContext.Provider>
  )
}

export const useThemePreference = (): ThemePreferenceValue => {
  const ctx = useContext(ThemePreferenceContext)
  if (!ctx) {
    throw new Error('useThemePreference phải dùng trong ThemePreferenceProvider')
  }
  return ctx
}
